/**
 * Each car's stints on the race clock, for the Strategy tab's time chart (plan item 5): when each
 * stint started and ended in seconds from the race start, its laps and its driver.
 *
 * A race with an analysis package brings its stints with the chart data (CarData.stints, D-STINT),
 * pit-out to pit-in from the timing's pit-lane clocks where it has them. Without one they are built
 * here from the pit markers and the line crossings, the engine's rule for timing with no pit-lane
 * clocks (server analysis/engine/stints.ts, WRL): a stint ends where its in-lap crosses the line and
 * the next one starts there. Drive-throughs don't end a stint; they are marked on it.
 *
 * Times: crossing clock times when the race has them (gap-math crossingTimes, seconds from
 * midnight, carried over midnight), measured from the race start: the median over the cars of
 * their lap 1 crossing less their lap 1 time. Otherwise each car's lap times added up from lap 1
 * (the chart's time axis, raceClock), so the race starts at 0.
 */
import type { AnnotationData, CarData, CarStint, RaceChartData } from "@shared/types";
import { carAtPosition, crossingTimes, usesClock } from "./gap-math";
import { raceClock, raceStops, type Stop } from "./live-strategy";
import { expandDriverName } from "../my-drivers/api";

/** A car whose first lap starts this long after the race start joined late (engine LATE_JOIN_S). */
export const LATE_JOIN_S = 300;

export interface ClockStint {
  /** 1 for the car's first stint. */
  n: number;
  startLap: number;
  endLap: number;
  /** Seconds from the race start. */
  startS: number;
  endS: number;
  driver: string | null;
  start: CarStint["start"];
  /** "running": the last stint of a car still out (RaceTrace Live). */
  end: CarStint["end"] | "running";
  /** The stop that ended it, when one did: its kind, caution, time lost and pit-lane time. */
  stop?: Pick<Stop, "kind" | "caution" | "lostS" | "laneS" | "splash">;
}

export interface CarOnClock {
  stints: ClockStint[];
  /** Drive-throughs: in the pit lane without stopping, mid-stint (the in-lap and when it crossed). */
  driveThroughs: { lap: number; atS: number }[];
  /** Where its stints came from: the race's analysis package, or worked out from the timing. */
  source: "package" | "timing";
}

export interface RaceOnClock {
  cars: Map<string, CarOnClock>;
  /** Caution periods on the race clock, from the leader's crossings (s from the race start). */
  cautions: { startS: number; endS: number }[];
  /** The race so far: the latest crossing (s from the race start). */
  endS: number;
}

const median = (xs: number[]) => {
  const s = [...xs].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
};

const startCache = new WeakMap<RaceChartData, number>();

/** When the race started on its crossing clock: each car's lap 1 crossing less its lap 1 time, the median. */
function clockStart(data: RaceChartData): number {
  let v = startCache.get(data);
  if (v === undefined) {
    const starts: number[] = [];
    for (const c of Object.values(data.cars)) {
      const l1 = c.laps.find((l) => l.l === 1);
      const t = crossingTimes(data, c.num).get(1);
      if (l1 && t !== undefined && l1.ltSec > 0) starts.push(t - l1.ltSec);
    }
    v = starts.length ? median(starts) : 0;
    startCache.set(data, v);
  }
  return v;
}

/** When the car crossed the line at the end of each lap, in seconds from the race start. */
export function lapEndTimes(data: RaceChartData, key: string): Map<number, number> {
  const car = data.cars[key];
  if (!car) return new Map();
  if (!usesClock(data)) return raceClock(data).get(key) ?? new Map();
  const t0 = clockStart(data);
  return new Map([...crossingTimes(data, car.num)].map(([l, t]) => [l, t - t0]));
}

/** The latest crossing at or before a lap (a lap the timing missed has none). */
function atOrBefore(times: Map<number, number>, lap: number): number | null {
  for (let l = lap; l >= 0; l--) {
    if (l === 0) return 0;
    const t = times.get(l);
    if (t !== undefined) return t;
  }
  return null;
}

const ENDS_STINT = (s: Stop) => s.kind === "stop" || s.kind === "garage";

function fromTiming(
  data: RaceChartData,
  annotations: AnnotationData | null,
  key: string,
  stops: Stop[],
  finishS: number,
  live: boolean
): CarOnClock {
  const car = data.cars[key];
  const times = lapEndTimes(data, key);
  const laps = [...car.laps].sort((a, b) => a.l - b.l);
  const last = laps[laps.length - 1];
  const markers = annotations?.[key]?.pits ?? [];
  const marker = (lap: number) => markers.find((m) => m.l === lap);
  const named = (n?: string) => {
    const t = n?.trim();
    return t ? expandDriverName(t, car.drivers ?? []) : null;
  };
  const retiredAt = markers.find((m) => m.kind === "retirement")?.l ?? null;
  const ends = stops.filter((s) => ENDS_STINT(s) && s.lap < last.l);

  const stints: ClockStint[] = [];
  // Its first stint starts at the green flag, unless its first lap started well after it (from the
  // garage or the pit lane: the engine's late join).
  const first = laps[0];
  const firstEnd = times.get(first.l);
  const firstStart =
    firstEnd !== undefined && first.ltSec > 0 ? firstEnd - first.ltSec : 0;
  const late = first.l === 1 && firstStart > LATE_JOIN_S;
  let startS = late ? firstStart : 0;
  let start: ClockStint["start"] = late ? "pit_lane_start" : "race_start";
  let startLap = first.l;
  let driver: string | null = null;
  for (const s of ends) {
    const endS = atOrBefore(times, s.lap) ?? startS;
    const m = marker(s.lap);
    stints.push({
      n: stints.length + 1,
      startLap,
      endLap: s.lap,
      startS,
      endS,
      driver: named(m?.inDriver) ?? driver,
      start,
      end: "pit_in",
      stop: {
        kind: s.kind,
        caution: s.caution,
        lostS: s.lostS,
        laneS: s.laneS ?? null,
        ...(s.splash ? { splash: true } : {}),
      },
    });
    startS = endS;
    start = "pit_out";
    startLap = s.lap + 1;
    driver = named(m?.outDriver);
  }
  const lastS = atOrBefore(times, last.l) ?? startS;
  const end: ClockStint["end"] =
    retiredAt !== null
      ? "retired"
      : lastS >= finishS - 1e-6
        ? "chequered"
        : live
          ? "running"
          : "retired";
  stints.push({
    n: stints.length + 1,
    startLap,
    endLap: last.l,
    startS,
    endS: lastS,
    driver,
    start,
    end,
  });

  const driveThroughs = stops
    .filter((s) => s.kind === "drive_through")
    .map((s) => ({ lap: s.lap, atS: atOrBefore(times, s.lap) ?? 0 }));
  return { stints, driveThroughs, source: "timing" };
}

function fromPackage(
  car: CarData,
  stints: CarStint[],
  stops: Stop[],
  times: Map<number, number>
): CarOnClock {
  const byEnd = new Map(stops.filter(ENDS_STINT).map((s) => [s.lap, s]));
  return {
    stints: stints.map((t) => {
      const s = t.end === "pit_in" ? byEnd.get(t.endLap) : undefined;
      return {
        n: t.n,
        startLap: t.startLap,
        endLap: t.endLap,
        startS: t.startS,
        endS: t.endS,
        driver: t.driver ? expandDriverName(t.driver, car.drivers ?? []) : null,
        start: t.start,
        end: t.end,
        ...(s
          ? {
              stop: {
                kind: s.kind,
                caution: s.caution,
                lostS: s.lostS,
                laneS: s.laneS ?? null,
                ...(s.splash ? { splash: true } : {}),
              },
            }
          : {}),
      };
    }),
    driveThroughs: stops
      .filter((s) => s.kind === "drive_through")
      .map((s) => ({ lap: s.lap, atS: atOrBefore(times, s.lap) ?? 0 })),
    source: "package",
  };
}

const caches = {
  done: new WeakMap<RaceChartData, WeakMap<object, RaceOnClock>>(),
  live: new WeakMap<RaceChartData, WeakMap<object, RaceOnClock>>(),
};
const NONE = {};

/**
 * Every car's stints on the race clock, and the race's caution periods. `live`: the race is still
 * running, so a car's last stint without a flag is "running", not "retired". Worked out once per
 * chart and pit markers; callers must not change what it returns.
 */
export function stintsOnClock(
  data: RaceChartData,
  annotations: AnnotationData | null,
  opts: { live?: boolean } = {}
): RaceOnClock {
  const annKey: object = annotations ?? NONE;
  const live = !!opts.live;
  const cache = live ? caches.live : caches.done;
  let byAnn = cache.get(data);
  const hit = byAnn?.get(annKey);
  if (hit) return hit;

  const ends = new Map<string, Map<number, number>>();
  for (const k of Object.keys(data.cars)) ends.set(k, lapEndTimes(data, k));
  // The flag: the winner's last crossing (the first car to finish the most laps).
  let finishS = Infinity;
  let mostLaps = 0;
  for (const [k, c] of Object.entries(data.cars)) {
    const lastLap = c.laps.reduce((m, l) => Math.max(m, l.l), 0);
    const t = ends.get(k)!.get(lastLap);
    if (t === undefined) continue;
    if (lastLap > mostLaps || (lastLap === mostLaps && t < finishS)) {
      mostLaps = lastLap;
      finishS = t;
    }
  }
  let endS = 0;
  for (const m of ends.values()) for (const t of m.values()) endS = Math.max(endS, t);

  const { stopsBy } = raceStops(data, annotations);
  const cars = new Map<string, CarOnClock>();
  for (const [k, c] of Object.entries(data.cars)) {
    if (!c.laps.length) continue;
    const stops = stopsBy.get(k) ?? [];
    cars.set(
      k,
      c.stints?.length
        ? fromPackage(c, c.stints, stops, ends.get(k)!)
        : fromTiming(data, annotations, k, stops, finishS, live)
    );
  }

  // Cautions: from the leader's crossing before the first caution lap to its crossing at the last.
  const cautions: RaceOnClock["cautions"] = [];
  for (const [a, b] of data.fcy ?? []) {
    const leaderAt = (lap: number) => {
      if (lap <= 0) return 0;
      const n = carAtPosition(data, lap, 1, null);
      return n === null ? null : (ends.get(String(n))?.get(lap) ?? null);
    };
    const s = leaderAt(a - 1);
    const e = leaderAt(b);
    if (s !== null && e !== null && e > s) cautions.push({ startS: s, endS: e });
  }

  const out: RaceOnClock = { cars, cautions, endS };
  if (!byAnn) cache.set(data, (byAnn = new WeakMap()));
  byAnn.set(annKey, out);
  return out;
}
